/**
 * List Style Fields - Reusable templates for list item styling
 *
 * Based on SingleSelectionList/MultipleSelectionList schemas.
 * Maximally reuses existing field patterns from textStyleFields and backgroundStyleFields.
 *
 * Sprint 8: List style fields population
 * Sprint 9: Fix list position fields, add padding field factories
 */

import type { FieldConfig } from '../../types/editorFieldTypes';
import { NUMBER_0_50_OPTIONS, BOOLEAN_OPTIONS, CHECKBOX_KIND_OPTIONS } from './options';

// ============================================
// Vertical Alignment Options
// ============================================

const VERTICAL_ALIGNMENT_OPTIONS = [
  { value: 'top', label: 'top' },
  { value: 'center', label: 'center' },
  { value: 'bottom', label: 'bottom' },
] as const;

// ============================================
// Item State Style Fields (selected/unselected)
// ============================================

/**
 * Base item state style fields template
 * Reuses same structure as BUTTON_CONTAINER_STYLE_FIELDS for consistency
 *
 * Schema paths:
 *   - Unselected: list.styles.{property}
 *   - Selected: list.selectedBlock.styles.{property}
 */
const ITEM_STATE_STYLE_TEMPLATE: FieldConfig[] = [
  {
    path: '.backgroundColor',
    label: 'background color',
    type: 'color',
    defaultValue: '#ffc201',
    isNullable: true,
  },
  {
    path: '.borderWidth',
    label: 'border width',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 1,
    defaultScrollValue: 1,
    isNullable: true,
  },
  {
    path: '.borderColor',
    label: 'border color',
    type: 'color',
    defaultValue: '#606060',
    isNullable: true,
  },
  {
    path: '.borderRadius',
    label: 'border radius',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 12,
    defaultScrollValue: 12,
    isNullable: true,
  },
];

/**
 * Create item state style fields with full path prefix
 *
 * @param basePath - Full path to styles object (e.g., 'list.styles' or 'list.selectedBlock.styles')
 * @returns Array of FieldConfig with full paths
 *
 * @example
 * createListItemStateFields('list.styles')
 * // Returns fields with paths like 'list.styles.backgroundColor'
 *
 * createListItemStateFields('list.selectedBlock.styles')
 * // Returns fields with paths like 'list.selectedBlock.styles.backgroundColor'
 */
export function createListItemStateFields(basePath: string): FieldConfig[] {
  return ITEM_STATE_STYLE_TEMPLATE.map((field) => ({
    ...field,
    path: `${basePath}${field.path}`,
  }));
}

// ============================================
// Checkbox Style Fields
// ============================================

/**
 * Checkbox style fields template
 * Used for MultipleSelection list types with checkbox variants
 *
 * Schema paths based on CheckBox interface:
 * - kind: CheckBoxKind ('square' | 'circle')
 * - styles: CheckBoxBlock (default/unchecked state)
 * - selectedBlock.styles: CheckBoxBlock (selected/checked state)
 */
const CHECKBOX_STYLE_TEMPLATE: FieldConfig[] = [
  {
    path: '.kind',
    label: 'shape',
    type: 'select',
    options: [...CHECKBOX_KIND_OPTIONS],
    defaultValue: 'square',
    isNullable: false, // kind is required in schema
  },
  {
    path: '.selectedBlock.styles.color',
    label: 'checked color',
    type: 'color',
    defaultValue: '#ffc201',
    isNullable: true,
  },
  {
    path: '.styles.color',
    label: 'unchecked color',
    type: 'color',
    defaultValue: '#ffffff',
    isNullable: true,
  },
];

/**
 * Create checkbox style fields with full path prefix
 *
 * @param basePath - Base path to checkbox (e.g., 'list.items.0.checkBox')
 * @returns Array of FieldConfig with full paths
 */
export function createListCheckboxStyleFields(basePath: string): FieldConfig[] {
  return CHECKBOX_STYLE_TEMPLATE.map((field) => ({
    ...field,
    path: `${basePath}${field.path}`,
  }));
}

// ============================================
// List Position Fields
// ============================================

/**
 * List position style fields template
 * Controls vertical alignment and max cell height
 *
 * Schema paths: list.styles.{verticalAlignment, useMaxCellHeight}
 */
const LIST_POSITION_TEMPLATE: FieldConfig[] = [
  {
    path: '.styles.verticalAlignment',
    label: 'vertical alignment',
    type: 'select',
    options: [...VERTICAL_ALIGNMENT_OPTIONS],
    defaultValue: 'center',
    isNullable: true,
  },
  {
    path: '.styles.useMaxCellHeight',
    label: 'max cell height',
    type: 'select',
    options: [...BOOLEAN_OPTIONS],
    defaultValue: 'false',
    isNullable: true,
  },
];

/**
 * Create list position style fields with full path prefix
 *
 * @param basePath - Base path to list (e.g., 'list')
 * @returns Array of FieldConfig with full paths
 */
export function createListPositionFields(basePath: string): FieldConfig[] {
  return LIST_POSITION_TEMPLATE.map((field) => ({
    ...field,
    path: `${basePath}${field.path}`,
  }));
}

